import { EMPTY_SPACER_KEY, getEmptySpacerItem } from './commandBarItems/getEmptySpacerItem';
import { getShareMenuItem } from './commandBarItems/getShareMenuItem';
import { getSurfaceNavigationMenuItems } from './commandBarItems/getSurfaceNavigationMenuItems';
import { getSurfacePrintMenuItem } from './commandBarItems/getSurfacePrintMenuItem';
import { getSurfaceTimestripMenuItem } from './commandBarItems/getSurfaceTimestripMenuItem';
import { getSurfaceTodayMenuItem } from './commandBarItems/getSurfaceTodayMenuItem';
import { getViewSwitcherMenuItem } from './commandBarItems/getViewSwitcherMenuItem';
import { getHamburgerItem } from './commandBarItems/getHamburgerItem';
import { getNewEventItem } from './commandBarItems/getNewEventItem';
import { CalendarModuleCommandBarProps } from './commandBarItems/schema';
import { IContextualMenuRenderItem } from '@fluentui/react/lib/ContextualMenu';
import { observer } from 'mobx-react-lite';
import { shouldShowLeftNavAsOverlay, getIsLeftNavShown } from 'owa-calendar-layout';
import { isCanvasViewTypeSelected } from 'owa-calendar-module-store';
import { commandBarEllipsisAriaLabel } from 'owa-calendar-shared-strings/lib/strings/calendarSurfaceCommandBar.locstring.json';
import { FilterableCommandBar, FilterableMenuItem } from 'owa-filterable-menu';
import loc from 'owa-localize';
import * as React from 'react';
import { isFeatureEnabled } from 'owa-feature-flags';

const styles = require('./CalendarModuleCommandBar.scss');
const classNames = require('classnames');

export default observer(function CalendarModuleCommandBar(props: CalendarModuleCommandBarProps) {
    let primaryItems: FilterableMenuItem[] = [];
    let farItems: FilterableMenuItem[] = [];

    const shouldShowHamburgerAndNewEventItems =
        isFeatureEnabled('tri-metaOsRail') &&
        (!getIsLeftNavShown() || shouldShowLeftNavAsOverlay());

    const viewSwitcherButtonRef = React.useRef<IContextualMenuRenderItem>(null);
    if (isCanvasViewTypeSelected()) {
        primaryItems = shouldShowHamburgerAndNewEventItems
            ? [getHamburgerItem(), getNewEventItem()]
            : [getEmptySpacerItem()];
        farItems = [getViewSwitcherMenuItem(props, viewSwitcherButtonRef), getShareMenuItem()];
    } else {
        primaryItems = [
            getSurfaceTodayMenuItem(),
            ...getSurfaceNavigationMenuItems(props),
            getSurfaceTimestripMenuItem(props),
        ];
        farItems = [
            getViewSwitcherMenuItem(props, viewSwitcherButtonRef),
            getShareMenuItem(),
            getSurfacePrintMenuItem(),
        ];

        if (shouldShowHamburgerAndNewEventItems) {
            primaryItems.unshift(getHamburgerItem(), getNewEventItem());
        }
    }

    const commandBarItems: FilterableMenuItem[] = [
        ...primaryItems,
        // this marks the split between the primary items and the farItems in the surface command bar
        ...farItems,
    ];
    commandBarItems
        .filter(item => item.key !== EMPTY_SPACER_KEY)
        .forEach((item, index, array) => {
            if (item) {
                item['aria-setsize'] = array.length;
                item['aria-posinset'] = index + 1; //posinset is 1 based
            }
        });

    const CalendarModuleCommandBarStyleProps = {
        root: {
            /*
             * this glocal css prop selects the nth element of the overflowSet and pushes all nth
             * elements to the right of the component, this effectively separates the primary and far items
             * of the surface command bar
             */
            selectors: {
                [`& .ms-OverflowSet-item:nth-child(${primaryItems.length})`]: {
                    display: 'flex',
                    flexGrow: 1,
                },
            },
            'padding-left': 0,
        },
    };

    const calendarSidePanelIsCollapsed = !getIsLeftNavShown();

    return (
        <FilterableCommandBar
            className={classNames(
                styles.commandBar,
                (calendarSidePanelIsCollapsed || shouldShowLeftNavAsOverlay()) &&
                    !isFeatureEnabled('tri-metaOsRail') &&
                    styles.calendarSidePanelIsHidden
            )}
            filterableItems={commandBarItems}
            elipisisAriaLabel={loc(commandBarEllipsisAriaLabel)}
            styles={CalendarModuleCommandBarStyleProps}
        />
    );
});
